'use client';

import type {
  MutationFunction,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ErrorType } from '../utils/fetch';
import type { AuthLoginRequest, AuthLoginResponse } from './login';
import { authLogin } from './login';

// Kept out of ./login so `authLogin` itself stays importable without pulling in
// react-query — a server-side or plain-fetch caller has no use for the hook.

export type AuthLoginVariables = { data: AuthLoginRequest };
export type AuthLoginMutationResult = NonNullable<AuthLoginResponse>;
export type AuthLoginMutationBody = AuthLoginRequest;
export type AuthLoginMutationError = unknown;

export const getAuthLoginMutationOptions = <TError = ErrorType<unknown>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<AuthLoginResponse, TError, AuthLoginVariables, TContext>;
  },
  meta?: Record<string, unknown>,
): UseMutationOptions<AuthLoginResponse, TError, AuthLoginVariables, TContext> => {
  const { mutation: mutationOptions } = options ?? {};

  const mutationFn: MutationFunction<AuthLoginResponse, AuthLoginVariables> = (props) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- nullish check
    const { data } = props ?? {};

    return authLogin(data, { meta });
  };

  return { mutationFn, ...mutationOptions };
};

/**
 * The login mutation, shared by carbon's no-code login action and the app-builder SDK.
 *
 * Both packages resolve to the same `@tanstack/react-query` module — `@unifyapps/network/react-query`
 * is a bare re-export of it — so there is no second client instance to worry about.
 *
 * `meta` is read off the QueryClient's defaults rather than taken as an argument: a
 * mutationFn, unlike a queryFn, is handed no meta from react-query's context, so the
 * routing an app configures there (apiDomain + the `x-ua-app` interface header) would
 * otherwise be dropped. Reading it here is a no-op for callers that set none.
 */
export const useAuthLogin: <TError = ErrorType<unknown>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<AuthLoginResponse, TError, AuthLoginVariables, TContext>;
}) => UseMutationResult<AuthLoginResponse, TError, AuthLoginVariables, TContext> = (options) => {
  const meta = useQueryClient().getDefaultOptions().mutations?.meta;

  return useMutation(getAuthLoginMutationOptions(options, meta));
};
